import { useHasPermission } from '@app/core/auth/authorization/hooks/useHasPermission';
import { isOwnedStation } from '@app/core/stations/helpers';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportConnectedForStation } from '@app/modules/social-export/hooks/useSocialExportConnectedForStation';
import { fragmentsPermissions } from '@mi-front/api-clients';
import { Box, Grid, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ExportControl } from '../export/ExportControl';
import { QuickPlatformUploadFields } from './QuickPlatformUploadFields';
import { Station } from '@app/core/types/stations';

type Props = {
  station: Station;
  durationMs?: number;
  allowExternalExport?: boolean;
};

function ConnectedPlatformGridItem({
  stationId,
  platform,
  durationMs,
}: {
  stationId: string;
  platform: SocialPlatform;
  durationMs?: number;
}) {
  const connected = useSocialExportConnectedForStation(stationId, platform);
  if (!connected) return null;
  return (
    <Grid item xs={12} xl={6}>
      <QuickPlatformUploadFields
        stationId={stationId}
        platform={platform}
        durationMs={durationMs}
      />
    </Grid>
  );
}

export function QuickFragmentExportFields({ station, durationMs, allowExternalExport }: Props) {
  const hasSocialPublish = useHasPermission(fragmentsPermissions.Fragments_SocialPublish);

  if (!isOwnedStation(station) || (!hasSocialPublish && !allowExternalExport)) {
    return null;
  }

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          letterSpacing: '0.09em',
          fontSize: '11px',
          fontWeight: 500,
        }}
      >
        <FormattedMessage id="fragments.form.publishOn" defaultMessage="Publicar rápido" />
      </Typography>
      <Grid container spacing={1} mt={0}>
        {hasSocialPublish &&
          socialPlatforms
            .all()
            .map((platform) => (
              <ConnectedPlatformGridItem
                key={platform.id}
                stationId={station.id}
                platform={platform}
                durationMs={durationMs}
              />
            ))}
        {allowExternalExport && (
          <Grid item xs={12} xl={6}>
            <ExportControl />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
